import type { PracticaDefinition, ProjectDefinition } from "../types";
import { project as proyectoContarSi } from "./funciones/agregacion/contar-si-conjunto";
import { project as proyectoPromedioSi } from "./funciones/agregacion/promedio-si-conjunto";
import { project as proyectoSumarSi } from "./funciones/agregacion/sumar-si-conjunto";
import { project as proyectoTablero } from "./funciones/agregacion/tablero";
import { project as proyectoFiltrar } from "./funciones/matrices/filtrar";
import { project as proyectoOrdenar } from "./funciones/matrices/ordenar";
import { project as proyectoReporteMatrices } from "./funciones/matrices/reporte";
import { project as proyectoSecuencia } from "./funciones/matrices/secuencia";
import { project as proyectoUnicos } from "./funciones/matrices/unicos";
import { project as proyectoBuscarv } from "./funciones/busqueda/buscarv";
import { project as proyectoCoincidirx } from "./funciones/busqueda/coincidirx";
import { project as proyectoCotizador } from "./funciones/busqueda/cotizador";
import { project as proyectoIndiceCoincidir } from "./funciones/busqueda/indice-coincidir";
import { project as proyectoAntesDespues } from "./funciones/texto/antes-despues";
import { project as proyectoDividir } from "./funciones/texto/dividir";
import { project as proyectoErp } from "./funciones/texto/erp";
import { project as proyectoExtraer } from "./funciones/texto/extraer";
import { project as proyectoLimpiar } from "./funciones/texto/limpiar";
import { project as proyectoAntiguedad } from "./funciones/fechas/antiguedad";
import { project as proyectoComponentes } from "./funciones/fechas/componentes";
import { project as proyectoContratos } from "./funciones/fechas/contratos";
import { project as proyectoDiasHabiles } from "./funciones/fechas/dias-habiles";
import { project as proyectoTurnos } from "./funciones/fechas/turnos";
import { project as proyectoApilar } from "./funciones/avanzadas/apilar";
import { project as proyectoElegir } from "./funciones/avanzadas/elegir";
import { project as proyectoEncol } from "./funciones/avanzadas/encol";
import { project as proyectoReporteTrimestral } from "./funciones/avanzadas/reporte-trimestral";
import { project as proyectoTomar } from "./funciones/avanzadas/tomar";
import { project as proyectoEstadistica } from "./funciones/matematicas/estadistica-salarios";
import { project as proyectoJerarquia } from "./funciones/matematicas/jerarquia";
import { project as proyectoProveedores } from "./funciones/matematicas/proveedores";
import { project as proyectoRedondeo } from "./funciones/matematicas/redondeo";
import { project as proyectoSumaproducto } from "./funciones/matematicas/sumaproducto";
import { project as proyectoAnidadas } from "./funciones/logicas/anidadas";
import { project as proyectoEsValidacion } from "./funciones/logicas/es-validacion";
import { project as proyectoSi } from "./funciones/logicas/si";
import { project as proyectoSiConjunto } from "./funciones/logicas/si-conjunto";
import { project as proyectoSiError } from "./funciones/logicas/si-error";
import { project as proyectoYONo } from "./funciones/logicas/y-o-no";

export type FuncionPrioridad = "Esencial" | "Alta";

/**
 * A category of functions. It behaves like a practice (modes, exam,
 * progress), but is listed on the Funciones page instead of the home list.
 */
export interface FuncionCategoria extends PracticaDefinition {
  priority: FuncionPrioridad;
}

const REQ_2021 =
  "<strong>Microsoft 365</strong> o <strong>Excel 2021</strong> o posterior.";
const REQ_2024 =
  "<strong>Microsoft 365</strong> o <strong>Excel 2024</strong> (estas funciones no existen en Excel 2021).";
const REQ_BASICS = [
  "Conocimientos básicos: seleccionar rangos y escribir fórmulas simples.",
  'Complemento <strong>"Modo Imparable"</strong> instalado y activo.',
];

function categoria(
  id: string,
  title: string,
  functions: string,
  priority: FuncionPrioridad,
  description: string,
  projects: ProjectDefinition[],
  requirement = REQ_2021
): FuncionCategoria {
  return {
    id: `funciones-${id}`,
    title,
    subtitle: functions,
    fullTitle: `${title}: ${functions}`,
    description,
    requirements: [requirement, ...REQ_BASICS],
    projects,
    priority,
  };
}

/* ═══════════════════════  BUSCARX  ═══════════════════════ */
const proyectoBuscarx: ProjectDefinition = {
  id: "funciones-buscarx",
  title: "BUSCARX: Central de Pedidos",
  subtitle: "Búsquedas exactas, aproximadas, a la izquierda y en dos sentidos",
  description:
    "Trabajas en el área de operaciones de una tienda de tecnología.\n\nEl catálogo, los pedidos y las tablas de comisiones viven en hojas distintas. Tu misión es conectarlos con BUSCARX, desde la búsqueda más simple hasta búsquedas anidadas.",
  topics: [
    "Coincidencia exacta y valor si no se encuentra",
    "Búsqueda a la izquierda y varias columnas",
    "Tramos, comodines y última coincidencia",
    "BUSCARX anidado (dos sentidos)",
  ],
  tasks: [
    {
      id: 1,
      level: "basico",
      title: "Tarea 1: Precio de un producto",
      description:
        "En la hoja <strong>BX Consultas</strong>, celda <strong>C4</strong>, usa <strong>BUSCARX</strong> para devolver el <strong>precio</strong> del SKU que está en <strong>B4</strong>, buscándolo en la tabla <strong>Catalogo</strong>.",
      solution: [
        "Ve a la hoja <strong>BX Consultas</strong> y selecciona la celda <span class=\"formula\">C4</span>.",
        "Escribe <span class=\"formula\">=BUSCARX(B4,</span> — el primer argumento es <strong>qué</strong> buscas.",
        "Agrega <span class=\"formula\">Catalogo[SKU],</span> — <strong>dónde</strong> lo buscas.",
        "Agrega <span class=\"formula\">Catalogo[Precio])</span> — <strong>qué</strong> quieres que te devuelva, y presiona <span class=\"formula\">Enter</span>.",
      ],
      expectedResult: "2899",
      finalFormula: "=BUSCARX(B4,Catalogo[SKU],Catalogo[Precio])",
    },
    {
      id: 2,
      level: "basico",
      title: "Tarea 2: Completar el nombre de cada pedido",
      description:
        "En la hoja <strong>BX Pedidos</strong>, llena la columna <strong>Producto</strong> (de <strong>F4</strong> hacia abajo) con el nombre del producto de cada pedido, buscando su <strong>SKU</strong> en la tabla <strong>Catalogo</strong>.",
      solution: [
        "En la hoja <strong>BX Pedidos</strong>, selecciona la celda <span class=\"formula\">F4</span>.",
        "Escribe <span class=\"formula\">=BUSCARX([@SKU],Catalogo[SKU],Catalogo[Producto])</span>. <span class=\"formula\">[@SKU]</span> significa \"el SKU de esta misma fila\".",
        "Presiona <span class=\"formula\">Enter</span>. Como es una tabla, Excel copia la fórmula a toda la columna automáticamente.",
      ],
      expectedResult: "F4 = Mouse inalámbrico, F5 = Laptop Pro 14, …",
      finalFormula: "=BUSCARX([@SKU],Catalogo[SKU],Catalogo[Producto])",
    },
    {
      id: 3,
      level: "basico",
      title: "Tarea 3: Cuando el SKU no existe",
      description:
        "En <strong>BX Consultas</strong>, celda <strong>C6</strong>, busca el SKU de <strong>B6</strong> en el catálogo y devuelve su producto. Ese SKU <strong>no existe</strong>: en lugar de <strong>#N/D</strong> la fórmula debe mostrar el texto <strong>No encontrado</strong>.",
      solution: [
        "Selecciona <span class=\"formula\">C6</span> en <strong>BX Consultas</strong>.",
        "BUSCARX tiene un cuarto argumento, <strong>si_no_se_encuentra</strong>, que se usa cuando no hay coincidencia.",
        "Escribe <span class=\"formula\">=BUSCARX(B6,Catalogo[SKU],Catalogo[Producto],\"No encontrado\")</span> y presiona <span class=\"formula\">Enter</span>.",
      ],
      expectedResult: "No encontrado",
      finalFormula: "=BUSCARX(B6,Catalogo[SKU],Catalogo[Producto],\"No encontrado\")",
    },
    {
      id: 4,
      level: "intermedio",
      title: "Tarea 4: Buscar hacia la izquierda",
      description:
        "En <strong>BX Consultas</strong>, celda <strong>C8</strong>, devuelve el <strong>SKU</strong> del producto escrito en <strong>B8</strong>. Ojo: en el catálogo la columna SKU está a la <strong>izquierda</strong> de Producto (algo que BUSCARV no puede hacer).",
      solution: [
        "Selecciona <span class=\"formula\">C8</span>.",
        "Ahora buscas en la columna <strong>Producto</strong> y devuelves la columna <strong>SKU</strong>: con BUSCARX el orden de las columnas no importa.",
        "Escribe <span class=\"formula\">=BUSCARX(B8,Catalogo[Producto],Catalogo[SKU])</span> y presiona <span class=\"formula\">Enter</span>.",
      ],
      expectedResult: "TEC-003",
      finalFormula: "=BUSCARX(B8,Catalogo[Producto],Catalogo[SKU])",
    },
    {
      id: 5,
      level: "intermedio",
      title: "Tarea 5: Ficha completa con una sola fórmula",
      description:
        "En <strong>BX Consultas</strong>, celda <strong>C10</strong>, escribe <strong>una sola</strong> fórmula que devuelva el <strong>Producto</strong>, la <strong>Categoría</strong> y el <strong>Proveedor</strong> del SKU de <strong>B10</strong>. El resultado debe ocupar <strong>C10:E10</strong> automáticamente.",
      solution: [
        "Selecciona <span class=\"formula\">C10</span>.",
        "Si el rango de resultado tiene varias columnas, BUSCARX devuelve todas y el resultado se <strong>derrama</strong> hacia la derecha.",
        "Escribe <span class=\"formula\">=BUSCARX(B10,Catalogo[SKU],Catalogo[[Producto]:[Proveedor]])</span> y presiona <span class=\"formula\">Enter</span>.",
        "Verás el resultado en <span class=\"formula\">C10</span>, <span class=\"formula\">D10</span> y <span class=\"formula\">E10</span>. Si aparece <strong>#DESBORDAMIENTO!</strong>, borra lo que haya en D10:E10.",
      ],
      expectedResult: "Cámara web HD | Periféricos | MegaParts",
      finalFormula: "=BUSCARX(B10,Catalogo[SKU],Catalogo[[Producto]:[Proveedor]])",
    },
    {
      id: 6,
      level: "intermedio",
      title: "Tarea 6: Comisión por tramos",
      description:
        "En <strong>BX Consultas</strong>, llena <strong>C13:C17</strong> con el <strong>% de comisión</strong> de cada vendedor según sus ventas (columna B), usando la tabla <strong>Comisiones</strong> (G3). Cada tramo aplica <strong>desde</strong> su monto: si las ventas no coinciden exactamente, toma el tramo inmediato inferior.",
      solution: [
        "Selecciona <span class=\"formula\">C13</span>.",
        "El quinto argumento de BUSCARX es <strong>modo_de_coincidencia</strong>. Con <span class=\"formula\">-1</span> busca la coincidencia exacta o, si no la hay, el valor inmediato <strong>menor</strong>.",
        "Escribe <span class=\"formula\">=BUSCARX(B13,Comisiones[Desde ventas],Comisiones[% comisión],,-1)</span>. Fíjate en las dos comas: dejamos vacío el argumento <strong>si_no_se_encuentra</strong>.",
        "Presiona <span class=\"formula\">Enter</span> y arrastra el controlador de relleno hasta <span class=\"formula\">C17</span>.",
      ],
      expectedResult: "2%, 6%, 8%, 4%, 10%",
      finalFormula: "=BUSCARX(B13,Comisiones[Desde ventas],Comisiones[% comisión],,-1)",
    },
    {
      id: 7,
      level: "intermedio",
      title: "Tarea 7: Buscar por parte del nombre",
      description:
        "En <strong>BX Consultas</strong>, celda <strong>C20</strong>, devuelve el nombre del <strong>primer producto</strong> del catálogo cuyo nombre <strong>contenga</strong> el texto de <strong>B20</strong>. Usa comodines.",
      solution: [
        "Selecciona <span class=\"formula\">C20</span>.",
        "El asterisco <span class=\"formula\">*</span> significa \"cualquier texto\". <span class=\"formula\">\"*\"&B20&\"*\"</span> busca B20 en cualquier parte del nombre.",
        "Para que BUSCARX entienda los comodines, el <strong>modo_de_coincidencia</strong> debe ser <span class=\"formula\">2</span>.",
        "Escribe <span class=\"formula\">=BUSCARX(\"*\"&B20&\"*\",Catalogo[Producto],Catalogo[Producto],,2)</span> y presiona <span class=\"formula\">Enter</span>.",
      ],
      expectedResult: "Silla ergonómica",
      finalFormula: "=BUSCARX(\"*\"&B20&\"*\",Catalogo[Producto],Catalogo[Producto],,2)",
    },
    {
      id: 8,
      level: "reto",
      title: "Tarea 8: El pedido más reciente",
      description:
        "En <strong>BX Consultas</strong>, celda <strong>C22</strong>, devuelve la <strong>fecha del último pedido</strong> del SKU de <strong>B22</strong>. Ese SKU aparece varias veces en <strong>Pedidos</strong> y los pedidos están ordenados por fecha: necesitas la <strong>última</strong> coincidencia, no la primera.",
      solution: [
        "Selecciona <span class=\"formula\">C22</span>.",
        "El sexto argumento de BUSCARX es <strong>modo_de_búsqueda</strong>. Con <span class=\"formula\">-1</span> busca de abajo hacia arriba, así encuentra primero el pedido más reciente.",
        "Escribe <span class=\"formula\">=BUSCARX(B22,Pedidos[SKU],Pedidos[Fecha],,0,-1)</span> y presiona <span class=\"formula\">Enter</span>.",
        "La celda ya tiene formato de fecha. Compara con la hoja <strong>BX Pedidos</strong> para comprobar que es el último pedido de ese SKU.",
      ],
      expectedResult: "11/03/2026",
      finalFormula: "=BUSCARX(B22,Pedidos[SKU],Pedidos[Fecha],,0,-1)",
    },
    {
      id: 9,
      level: "reto",
      title: "Tarea 9: Precio por producto y región",
      description:
        "En <strong>BX Consultas</strong>, celda <strong>C24</strong>, devuelve el precio del producto de <strong>B24</strong> en la región de <strong>B25</strong>, usando la tabla <strong>Precios por región</strong> (G11:J17). Necesitas <strong>dos BUSCARX</strong>, uno dentro del otro: uno elige la columna de la región y el otro la fila del producto.",
      solution: [
        "Selecciona <span class=\"formula\">C24</span>.",
        "Primero, el BUSCARX interior elige la <strong>columna</strong> de la región: <span class=\"formula\">BUSCARX(B25,H11:J11,H12:J17)</span> devuelve toda la columna de precios de esa región.",
        "Después, el BUSCARX exterior busca el producto y devuelve el precio de esa columna: <span class=\"formula\">=BUSCARX(B24,G12:G17,BUSCARX(B25,H11:J11,H12:J17))</span>.",
        "Presiona <span class=\"formula\">Enter</span>. Cambia B25 a otra región para ver cómo cambia el resultado.",
      ],
      expectedResult: "19599",
      finalFormula: "=BUSCARX(B24,G12:G17,BUSCARX(B25,H11:J11,H12:J17))",
    },
  ],
};

/** In learning order. Categories without projects show as "Próximamente". */
export const funcionesCategorias: FuncionCategoria[] = [
  categoria(
    "logicas",
    "Lógicas y condicionales",
    "SI, Y, O, NO, SI.CONJUNTO, SI.ERROR, SI.ND, ESNUMERO, ESTEXTO, ESBLANCO, ESERROR",
    "Esencial",
    "Toma decisiones dentro de tus fórmulas, valida tus datos y controla los errores. Primero cada función por separado; al final, un reto que las combina.",
    [
      proyectoSi,
      proyectoYONo,
      proyectoSiConjunto,
      proyectoSiError,
      proyectoEsValidacion,
      proyectoAnidadas,
    ]
  ),
  categoria(
    "busqueda",
    "Búsqueda y referencia",
    "BUSCARX, BUSCARV, INDICE, COINCIDIR, COINCIDIRX",
    "Esencial",
    "Conecta tablas y trae datos de una hoja a otra. Una función por proyecto y al final un cotizador que las combina.",
    [proyectoBuscarv, proyectoBuscarx, proyectoIndiceCoincidir, proyectoCoincidirx, proyectoCotizador]
  ),
  categoria(
    "agregacion",
    "Agregación condicional",
    "SUMAR.SI.CONJUNTO, CONTAR.SI.CONJUNTO, PROMEDIO.SI.CONJUNTO, MAX.SI.CONJUNTO, MIN.SI.CONJUNTO",
    "Esencial",
    "Suma, cuenta y promedia solo lo que cumple tus condiciones. Una función por proyecto y al final un tablero que las combina.",
    [proyectoSumarSi, proyectoContarSi, proyectoPromedioSi, proyectoTablero]
  ),
  categoria(
    "matrices-dinamicas",
    "Matrices dinámicas",
    "FILTRAR, UNICOS, ORDENAR, ORDENARPOR, SECUENCIA",
    "Esencial",
    "Fórmulas que devuelven tablas completas y se actualizan solas. Una función por proyecto y al final un reporte que las encadena con #.",
    [proyectoFiltrar, proyectoUnicos, proyectoOrdenar, proyectoSecuencia, proyectoReporteMatrices]
  ),
  categoria(
    "texto",
    "Texto y limpieza de datos",
    "EXTRAE, IZQUIERDA, DERECHA, ESPACIOS, SUSTITUIR, TEXTOANTES, TEXTODESPUES, DIVIDIRTEXTO",
    "Esencial",
    "Limpia y separa datos que llegan sucios de otros sistemas. Una función por proyecto y al final una exportación real de un ERP.",
    [proyectoExtraer, proyectoLimpiar, proyectoAntesDespues, proyectoDividir, proyectoErp],
    REQ_2024
  ),
  categoria(
    "fechas",
    "Fechas y tiempo",
    "FECHA, DIASEM, FECHA.MES, FIN.MES, SIFECHA, FRAC.AÑO, DIAS.LAB, DIA.LAB, RESIDUO",
    "Alta",
    "Calcula plazos, antigüedad, días hábiles y horas trabajadas sin contar a mano.",
    [proyectoComponentes, proyectoContratos, proyectoAntiguedad, proyectoDiasHabiles, proyectoTurnos]
  ),
  categoria(
    "matematicas",
    "Matemáticas y estadísticas",
    "REDONDEAR, SUMAPRODUCTO, MEDIANA, DESVEST.M, PERCENTIL.INC, JERARQUIA.EQV, K.ESIMO.MAYOR",
    "Alta",
    "Resume y compara números como un analista: redondeo, promedios ponderados, estadística y rankings.",
    [proyectoRedondeo, proyectoSumaproducto, proyectoEstadistica, proyectoJerarquia, proyectoProveedores]
  ),
  categoria(
    "matrices-avanzadas",
    "Funciones avanzadas de matrices",
    "ELEGIRCOLS, ELEGIRFILAS, TOMAR, EXCLUIR, APILARV, APILARH, ENCOL, AJUSTARFILAS, LET",
    "Alta",
    "Recorta, une y reacomoda tablas completas con una sola fórmula. Al final, LET para fórmulas legibles.",
    [proyectoElegir, proyectoTomar, proyectoApilar, proyectoEncol, proyectoReporteTrimestral],
    REQ_2024
  ),
];
